<template>
  <div
    class="emap"
    :class="{
      'emap-compact': compact,
      'emap-fit-stage': fitStage,
      'emap-two-finger-touch': twoFingerTouch,
    }"
    ref="wrap"
  >
    <div v-if="showControls || showViewToggle" class="emap-controls">
      <form v-if="showControls" class="emap-form index-ignore" @submit.prevent="runQuery">
        <input
          v-model="query"
          class="emap-input"
          type="text"
          autocomplete="off"
          spellcheck="false"
          placeholder="Ask a question..."
          aria-label="Question to place on the meaning map"
        >
        <button type="submit" class="emap-go" :disabled="busy">{{ busy ? '…' : 'Map it' }}</button>
      </form>
      <div v-if="showControls" class="emap-samples index-ignore">
        <button v-for="s in samples" :key="s" type="button" class="emap-chip" :disabled="busy" @click="query = s; runQuery();">{{ s }}</button>
      </div>
      <div v-if="showViewToggle" class="emap-view-toggle index-ignore" aria-label="Map projection">
        <button
          type="button"
          class="emap-view"
          :class="{ active: viewMode === '2d' }"
          @click="setViewMode('2d')"
        >2D</button>
        <button
          type="button"
          class="emap-view"
          :class="{ active: viewMode === '3d' }"
          @click="setViewMode('3d')"
        >3D</button>
      </div>
    </div>
    <div class="emap-stage">
      <canvas ref="canvas" class="emap-canvas" aria-label="Map of passages from across this site, arranged by meaning"></canvas>
      <button
        v-if="tooltip"
        type="button"
        class="emap-tooltip"
        :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
        @click.stop="goTo(tooltip.point)"
      >
        <span class="emap-tooltip-group">{{ tooltip.group }}</span>
        <span class="emap-tooltip-title">{{ tooltip.title }}</span>
      </button>
      <p v-if="status" class="emap-status">{{ status }}</p>
    </div>
    <div v-if="active && showHits" class="emap-hits index-ignore">
      <p class="emap-hits-label">Nearest passages to “{{ active.label }}”</p>
      <ol>
        <li v-for="h in active.list" :key="h.id">
          <button type="button" @click="goTo(h)">{{ h.title }}</button>
          <span class="emap-hit-score">{{ h.score.toFixed(2) }}</span>
        </li>
      </ol>
    </div>
    <p v-if="showCaption" class="emap-caption">
      Pinch to zoom, drag to pan, and click a dot to read the passage it represents.
    </p>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { ensureReady, semanticTopK } from '../utils/semanticSearch';
import { sourceRouteTarget } from '../utils/viewSourceTarget';

// Broad content families; finer than this and the map stops reading at a glance.
const GROUPS = [
  { key: 'profile', label: 'Profile', match: ['/about', '/education', '/experience', '/cv'] },
  { key: 'research', label: 'Research', match: ['/research'] },
  { key: 'projects', label: 'Builds', match: ['/projects', '/playbook', '/lexaday', '/dealtracker', '/stride', '/ragflow-pipeline', '/scrollytelling', '/early', '/aipersonalarchive', '/ai-it-operations'] },
  { key: 'travels', label: 'Travels', match: ['/travels'] },
  { key: 'fragments', label: 'Fragments', match: ['/fragments'] },
];

const LIGHT = {
  profile: '#8d8d8d',
  research: '#7b6f9e',
  projects: '#6f8f86',
  travels: '#9a7f62',
  fragments: '#a87786',
  label: '#757575',
  star: '#b3893d',
  edge: '#b3893d',
};

const DARK = {
  profile: '#9a9a9a',
  research: '#a89ccf',
  projects: '#8faea5',
  travels: '#c4a482',
  fragments: '#c795a6',
  label: '#a1a1a1',
  star: '#d9b36a',
  edge: '#d9b36a',
};

// Monochrome variants for the home hero: group dots stay distinguishable by
// lightness alone, and the star/edges drop the gold accent for a black-and-
// white look.
const MONO_LIGHT = {
  profile: '#565656',
  research: '#6f6f6f',
  projects: '#898989',
  travels: '#a3a3a3',
  fragments: '#bdbdbd',
  label: '#6f6f6f',
  star: '#1c1c1c',
  edge: '#8f8f8f',
};

const MONO_DARK = {
  profile: '#8a8a8a',
  research: '#9e9e9e',
  projects: '#b1b1b1',
  travels: '#c4c4c4',
  fragments: '#d7d7d7',
  label: '#a1a1a1',
  star: '#f5f5f5',
  edge: '#9a9a9a',
};

function groupOf(route) {
  for (const g of GROUPS) if (g.match.includes(route)) return g.key;
  return 'profile';
}

export default {
  name: 'EmbeddingMap',
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    showCaption: {
      type: Boolean,
      default: true,
    },
    showHits: {
      type: Boolean,
      default: true,
    },
    fitStage: {
      type: Boolean,
      default: false,
    },
    showViewToggle: {
      type: Boolean,
      default: true,
    },
    initialViewMode: {
      type: String,
      default: '2d',
    },
    allowNavigation: {
      type: Boolean,
      default: true,
    },
    allowZoom: {
      type: Boolean,
      default: true,
    },
    wheelZoomRequiresPinch: {
      type: Boolean,
      default: true,
    },
    lockPanOnTouch: {
      type: Boolean,
      default: true,
    },
    twoFingerTouch: {
      type: Boolean,
      default: false,
    },
    // Black-and-white rendering for the home hero; the search palette keeps
    // the colored family palette.
    monochrome: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['navigate', 'ready'],
  data() {
    return {
      query: '',
      busy: false,
      status: '',
      tooltip: null,
      active: null,
      viewMode: '2d',
      samples: ['exchange in Melbourne', "Henry's interests", 'growing up in Shanghai'],
    };
  },
  mounted() {
    this._points = [];
    this._transform = d3.zoomIdentity;
    this._introStart = 0;
    this._raf = null;
    this._spinRaf = null;
    this._focusRaf = null;
    this._hover = null;
    this._yaw = -0.72;
    this._pitch = 0.54;
    this._spinTime = 0;
    this._lastSpinTs = 0;
    this._keepActiveCentered = false;
    this._particles = [];
    this._particlesStart = 0;
    this._particlesDuration = 1300;
    this._particlesRaf = null;
    this._twoFingerGesture = null;
    this.viewMode = this.initialViewMode === '3d' ? '3d' : '2d';

    this.loadPoints();

    this._onResize = () => this.scheduleResize();
    window.addEventListener('resize', this._onResize, { passive: true });
    this._sizeObserver = new ResizeObserver(() => this.scheduleResize());
    this._sizeObserver.observe(this.$refs.wrap);

    this._themeObs = new MutationObserver(() => this.draw());
    this._themeObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  },
  beforeUnmount() {
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._spinRaf) cancelAnimationFrame(this._spinRaf);
    if (this._focusRaf) cancelAnimationFrame(this._focusRaf);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    if (this._particlesRaf) cancelAnimationFrame(this._particlesRaf);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this._sizeObserver?.disconnect();
    if (this._themeObs) this._themeObs.disconnect();
    this.unbindTwoFingerTouch();
  },
  methods: {
    palette() {
      const dark = document.body.classList.contains('dark-mode');
      if (this.monochrome) return dark ? MONO_DARK : MONO_LIGHT;
      return dark ? DARK : LIGHT;
    },
    async loadPoints() {
      try {
        const res = await fetch('/assets/embedding-map.json', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`embedding-map.json ${res.status}`);
        const data = await res.json();
        this._points = data.items.map((p) => ({ ...p, group: groupOf(p.route) }));
        this._byId = null;
        this.$nextTick(() => {
          this.resize();
          this.startIntro();
          if (this.viewMode === '3d') this.start3dMotion();
          if (this._pendingExternalHits) {
            const pending = this._pendingExternalHits;
            this._pendingExternalHits = null;
            this.showExternalHits(pending.rawHits, pending.opts);
          }
          this.$emit('ready');
        });
      } catch (e) {
        this.status = 'The map could not be loaded. Please try again.';
      }
    },
    scheduleResize() {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(() => {
        this._resizeRaf = null;
        this.resize();
      });
    },
    resize() {
      const wrap = this.$refs.wrap;
      const canvas = this.$refs.canvas;
      if (!wrap || !canvas) return;
      const w = wrap.clientWidth;
      if (!w) return;
      const h = this.fitStage ? this.fitHeight(w) : Math.max(340, Math.min(560, Math.round(w * 0.68)));
      const dpr = window.devicePixelRatio || 1;
      if (this._w && this._h) {
        this._transform = d3.zoomIdentity
          .translate(this._transform.x * w / this._w, this._transform.y * h / this._h)
          .scale(this._transform.k);
      }
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.height = `${h}px`;
      this._w = w;
      this._h = h;
      this._dpr = dpr;
      this._lastObservedWidth = w;
      this.syncZoomTransform();

      this.updateProjection();
      if (this.active) this.placeActive();

      if (this.allowZoom && !this._zoomBound) {
        this._zoomBound = true;
        this._zoom = d3.zoom()
          .scaleExtent([0.8, 9])
          .touchable(() => !this.twoFingerTouch)
          .filter((event) => {
            if (event.type === 'wheel' && this.wheelZoomRequiresPinch) return !!event.ctrlKey;
            return !event.ctrlKey || event.type === 'wheel';
          })
          .on('zoom', (ev) => {
            if (!this._applyingZoomTransform) {
              this._keepActiveCentered = false;
              if (this._focusRaf) {
                cancelAnimationFrame(this._focusRaf);
                this._focusRaf = null;
              }
            }
            // A single-finger drag on a touch device should never displace the
            // map. Keep the gesture scale while pinning the map to its centre.
            // Programmatic focus transitions have no source event, so they
            // retain their own translation.
            this._transform = this.shouldLockTouchPan(ev)
              ? this.centeredZoomTransform(ev.transform.k)
              : ev.transform;
            this.tooltip = null;
            this.draw();
          });
        d3.select(canvas).call(this._zoom);
      }
      if (this.twoFingerTouch && !this._twoFingerTouchBound) {
        this.bindTwoFingerTouch(canvas);
      }
      if (!this._pointerBound) {
        this._pointerBound = true;
        d3.select(canvas)
          .on('mousemove.emapHover', (ev) => this.onMove(ev))
          .on('mouseleave.emapHover', () => { this._hover = null; this.tooltip = null; this.draw(); })
          .on('click.emapClick', (ev) => this.onClick(ev));
      }
      this.draw();
    },
    shouldLockTouchPan(event) {
      if (!this.lockPanOnTouch || !event?.sourceEvent || typeof window === 'undefined') return false;
      const coarsePointer = window.matchMedia?.('(hover: none), (pointer: coarse)')?.matches;
      return navigator.maxTouchPoints > 0 && coarsePointer;
    },
    centeredZoomTransform(scale) {
      const centreX = (this._w || 0) / 2;
      const centreY = (this._h || 0) / 2;
      return d3.zoomIdentity
        .translate(centreX, centreY)
        .scale(scale)
        .translate(-centreX, -centreY);
    },
    bindTwoFingerTouch(canvas) {
      if (!canvas) return;
      this._twoFingerTouchBound = true;
      this._onTwoFingerStart = (event) => {
        if (event.touches.length < 2) return;
        event.preventDefault();
        this._keepActiveCentered = false;
        if (this._focusRaf) {
          cancelAnimationFrame(this._focusRaf);
          this._focusRaf = null;
        }
        const geometry = this.touchGeometry(event.touches);
        this._twoFingerGesture = {
          midpoint: geometry.midpoint,
          distance: geometry.distance,
          transform: this._transform || d3.zoomIdentity,
        };
        this.tooltip = null;
      };
      this._onTwoFingerMove = (event) => {
        if (!this._twoFingerGesture || event.touches.length < 2) return;
        event.preventDefault();
        const geometry = this.touchGeometry(event.touches);
        const start = this._twoFingerGesture;
        const nextScale = Math.max(0.8, Math.min(9,
          start.transform.k * (geometry.distance / Math.max(1, start.distance))));
        const worldX = (start.midpoint.x - start.transform.x) / start.transform.k;
        const worldY = (start.midpoint.y - start.transform.y) / start.transform.k;
        this._transform = d3.zoomIdentity
          .translate(
            geometry.midpoint.x - worldX * nextScale,
            geometry.midpoint.y - worldY * nextScale,
          )
          .scale(nextScale);
        this.syncZoomTransform();
        this._keepActiveCentered = false;
        this.draw();
      };
      this._onTwoFingerEnd = (event) => {
        if (!this._twoFingerGesture) return;
        if (event.touches.length >= 2) {
          const geometry = this.touchGeometry(event.touches);
          this._twoFingerGesture = {
            midpoint: geometry.midpoint,
            distance: geometry.distance,
            transform: this._transform || d3.zoomIdentity,
          };
          return;
        }
        this._twoFingerGesture = null;
        this._suppressClickUntil = Date.now() + 350;
      };
      canvas.addEventListener('touchstart', this._onTwoFingerStart, { passive: false });
      canvas.addEventListener('touchmove', this._onTwoFingerMove, { passive: false });
      canvas.addEventListener('touchend', this._onTwoFingerEnd, { passive: true });
      canvas.addEventListener('touchcancel', this._onTwoFingerEnd, { passive: true });
    },
    unbindTwoFingerTouch() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this._twoFingerTouchBound) return;
      canvas.removeEventListener('touchstart', this._onTwoFingerStart);
      canvas.removeEventListener('touchmove', this._onTwoFingerMove);
      canvas.removeEventListener('touchend', this._onTwoFingerEnd);
      canvas.removeEventListener('touchcancel', this._onTwoFingerEnd);
      this._twoFingerTouchBound = false;
      this._twoFingerGesture = null;
    },
    touchGeometry(touches) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      const first = touches[0];
      const second = touches[1];
      const firstX = first.clientX - rect.left;
      const firstY = first.clientY - rect.top;
      const secondX = second.clientX - rect.left;
      const secondY = second.clientY - rect.top;
      return {
        midpoint: {
          x: (firstX + secondX) / 2,
          y: (firstY + secondY) / 2,
        },
        distance: Math.hypot(secondX - firstX, secondY - firstY),
      };
    },
    fitHeight(w) {
      const wrap = this.$refs.wrap;
      const controls = wrap && wrap.querySelector('.emap-controls');
      const controlsH = controls ? controls.getBoundingClientRect().height + 8 : 0;
      const h = Math.floor((wrap && wrap.clientHeight ? wrap.clientHeight : 0) - controlsH);
      if (h > 120) return this.compact ? h : Math.max(220, h);
      return Math.max(260, Math.min(420, Math.round(w * 0.62)));
    },
    updateProjection() {
      if (!this._points.length || !this._w || !this._h) return;
      for (const p of this._points) {
        if (this.viewMode === '3d' && typeof p.x3 === 'number') {
          const projected = this.project3d(p);
          p.px = projected.x;
          p.py = projected.y;
          p.depth = projected.depth;
        } else {
          p.px = p.x * this._w;
          p.py = p.y * this._h;
          p.depth = 0;
        }
      }
      this._tree = d3.quadtree().x((p) => p.px).y((p) => p.py).addAll(this._points);
      this._centroids = GROUPS.map((g) => {
        const pts = this._points.filter((p) => p.group === g.key);
        if (!pts.length) return null;
        return {
          key: g.key,
          label: g.label,
          px: d3.mean(pts, (p) => p.px),
          py: d3.mean(pts, (p) => p.py),
        };
      }).filter(Boolean);
    },
    project3d(p) {
      const x = ((p.x3 ?? p.x) - 0.5) * 2;
      const y = ((p.y3 ?? p.y) - 0.5) * 2;
      const z = ((p.z3 ?? 0.5) - 0.5) * 2;
      const yaw = this._yaw;
      const pitch = this._pitch;
      const cy = Math.cos(yaw), sy = Math.sin(yaw);
      const cp = Math.cos(pitch), sp = Math.sin(pitch);
      const x1 = x * cy + z * sy;
      const z1 = -x * sy + z * cy;
      const y1 = y * cp - z1 * sp;
      const z2 = y * sp + z1 * cp;
      const perspective = 1 / (1 + z2 * 0.35);
      return {
        x: this._w / 2 + x1 * perspective * this._w * 0.37,
        y: this._h / 2 + y1 * perspective * this._h * 0.43,
        depth: z2,
      };
    },
    setViewMode(mode) {
      if (this.viewMode === mode) return;
      this.viewMode = mode;
      this.tooltip = null;
      this.updateProjection();
      if (this.active) this.placeActive();
      this.draw();
      if (mode === '3d') this.start3dMotion();
      else this.stop3dMotion();
    },
    start3dMotion() {
      this.stop3dMotion();
      this._spinTime = 0;
      this._lastSpinTs = performance.now();
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const step = () => {
        if (this.viewMode !== '3d') return;
        const now = performance.now();
        if (!this._hover && !this._twoFingerGesture) this._spinTime += now - this._lastSpinTs;
        this._lastSpinTs = now;
        this._yaw = -0.72 + this._spinTime * 0.00016;
        this.updateProjection();
        if (this.active) this.placeActive();
        if (this._keepActiveCentered && this.active && !this._focusRaf) this.setActiveFocusTransform();
        this.draw();
        this._spinRaf = requestAnimationFrame(step);
      };
      this._spinRaf = requestAnimationFrame(step);
    },
    stop3dMotion() {
      if (this._spinRaf) cancelAnimationFrame(this._spinRaf);
      this._spinRaf = null;
      this._keepActiveCentered = false;
      this._yaw = -0.72;
      this._pitch = 0.54;
    },
    // The reveal derives from wall-clock time, not accumulated frames, so a
    // throttled or hidden tab can never leave the map stuck half-drawn.
    introProgress() {
      if (!this._introStart) return 1;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 1;
      return Math.min(1, (performance.now() - this._introStart) / 900);
    },
    startIntro() {
      this._introStart = performance.now();
      const step = () => {
        this.draw();
        if (this.introProgress() < 1) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
      this.draw();
      // rAF never fires in background tabs; make sure the settled frame paints.
      setTimeout(() => this.draw(), 1000);
    },
    pointAt(ev) {
      const [mx, my] = d3.pointer(ev, this.$refs.canvas);
      const [dx, dy] = this._transform.invert([mx, my]);
      const r = 12 / this._transform.k;
      const p = this._tree && this._tree.find(dx, dy, r);
      return p ? { p, mx, my } : null;
    },
    onMove(ev) {
      const hit = this.pointAt(ev);
      this._hover = hit ? hit.p : null;
      this.$refs.canvas.style.cursor = hit ? 'var(--site-interactive-cursor, pointer)' : 'var(--site-grab-cursor, grab)';
      if (hit) {
        const g = GROUPS.find((x) => x.key === hit.p.group);
        this.tooltip = {
          x: Math.min(hit.mx + 14, this._w - 180),
          y: Math.max(hit.my - 12, 8),
          title: hit.p.title,
          group: g ? g.label : '',
          point: hit.p,
        };
      } else {
        this.tooltip = null;
      }
      this.draw();
    },
    onClick(ev) {
      if (this._suppressClickUntil && Date.now() < this._suppressClickUntil) return;
      if (!this.allowNavigation) return;
      const hit = this.pointAt(ev);
      if (!hit) return;
      if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) {
        this._hover = hit.p;
        const g = GROUPS.find((x) => x.key === hit.p.group);
        this.tooltip = {
          x: Math.min(hit.mx + 14, this._w - 180),
          y: Math.max(hit.my - 12, 8),
          title: hit.p.title,
          group: g ? g.label : '',
          point: hit.p,
        };
        this.draw();
        return;
      }
      this.goTo(hit.p);
    },
    goTo(p) {
      if (!this.allowNavigation) return;
      if (!p || !p.route) return;
      const destination = sourceRouteTarget(p.route, p.anchor);
      if (destination?.href) {
        window.open(destination.href, '_blank', 'noopener');
        return;
      }
      this.$emit('navigate', p);
      // Keep the anchor in the URL; the central afterEach landing smooth-scrolls
      // to it once the destination page has mounted.
      const target = { path: p.route };
      if (p.anchor) target.hash = `#${p.anchor}`;
      this.$router.push(target).catch(() => {});
    },
    async runQuery() {
      return this.placeQuery(this.query);
    },
    externalHits(rawHits) {
      const hits = [];
      for (const h of rawHits || []) {
        const id = h && h.id;
        const p = this.byId(id);
        if (!p) continue;
        hits.push({ id, score: typeof h.score === 'number' ? h.score : 0.8 });
      }
      return hits;
    },
    showExternalHits(rawHits, opts = {}) {
      if (!this._points.length) {
        this._pendingExternalHits = { rawHits, opts };
        return;
      }
      const hits = this.externalHits(rawHits);
      this._keepActiveCentered = !!opts.keepCentered;
      if (!hits.length) {
        this.active = null;
        this.tooltip = null;
        if (opts.zoomOut !== false) this.animateToFull(opts.duration || 700);
        else this.draw();
        return;
      }
      this.active = {
        label: opts.label || 'selected passages',
        hits,
        list: hits.slice(0, 5).map((h) => ({ ...this.byId(h.id), score: h.score })),
        born: performance.now(),
      };
      this.placeActive();
      if (opts.focus) this.animateToActive(opts.duration || 950);
      else if (opts.zoomOut !== false) this.animateToFull(opts.duration || 700);
      this.startPulse();
      // The "journey" moment: when the map settles on the answer's passages,
      // fling a burst of particles from the query star out to each hit.
      if (opts.focus) this.startParticles();
    },
    // A short-lived swarm of dots travelling from the query star to the nearest
    // passages, drawn on top of everything while it lasts. Reduced-motion users
    // get the constellation lines only, which already fade in on their own.
    startParticles() {
      if (!this.active || !this.active.hits.length) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this._particlesRaf) cancelAnimationFrame(this._particlesRaf);
      // The birth-pulse loop below is superseded by the particle loop, which also
      // redraws the growing constellation edges every frame.
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      this._particles = [];
      this._particlesStart = performance.now();
      const count = Math.max(4, Math.min(7, Math.round(72 / this.active.hits.length)));
      for (const h of this.active.hits) {
        const p = this.byId(h.id);
        if (!p) continue;
        for (let i = 0; i < count; i++) {
          this._particles.push({
            hitId: h.id,
            delayMs: Math.random() * 320,
            drift: (Math.random() - 0.5) * 12,
            size: 1.1 + Math.random() * 1.5,
            speed: 0.75 + Math.random() * 0.5,
            alpha: 0.5 + Math.random() * 0.4,
          });
        }
      }
      if (!this._particles.length) return;
      const step = () => {
        if (performance.now() - this._particlesStart >= this._particlesDuration) {
          this._particles = [];
          this._particlesRaf = null;
          // Hand back to the birth pulse so the constellation edges finish
          // growing (they animate for a little longer than the burst).
          this.startPulse();
          return;
        }
        this.draw();
        this._particlesRaf = requestAnimationFrame(step);
      };
      this._particlesRaf = requestAnimationFrame(step);
    },
    async placeQuery(rawQuery) {
      const q = String(rawQuery || '').trim();
      if (!q || this.busy || !this._points.length) return;
      this.query = q;
      this.busy = true;
      this.status = 'Preparing meaning search. The first use downloads a model to your browser…';
      try {
        await ensureReady();
        this.status = '';
        const hits = (await semanticTopK(q, 8)).filter((h) => this.byId(h.id));
        if (!hits.length) { this.status = 'No matching passages. Try another question.'; return; }
        this.active = {
          label: q,
          hits,
          list: hits.slice(0, 5).map((h) => ({ ...this.byId(h.id), score: h.score })),
          born: performance.now(),
        };
        this.placeActive();
        this.startPulse();
        // Same "journey" moment as the home Ask: particles fly from the star to
        // the nearest passages whenever a query lands on the map.
        this.startParticles();
      } catch (e) {
        this.status = 'Question placement is temporarily unavailable. You can still explore the map.';
      } finally {
        this.busy = false;
      }
    },
    // Star position is the similarity-weighted centroid of the hits, in the
    // current pixel space; recomputed whenever the canvas is resized.
    placeActive() {
      if (!this.active) return;
      let wx = 0, wy = 0, wsum = 0;
      for (const h of this.active.hits) {
        const p = this.byId(h.id);
        if (!p) continue;
        const w = Math.max(h.score, 0.01);
        wx += p.px * w; wy += p.py * w; wsum += w;
      }
      if (wsum > 0) {
        this.active.x = wx / wsum;
        this.active.y = wy / wsum;
      }
    },
    activeFocusTransform(scale) {
      if (!this.active || !this.active.hits.length || !this._w || !this._h) return d3.zoomIdentity;
      const pts = this.active.hits.map((h) => this.byId(h.id)).filter(Boolean);
      if (!pts.length) return d3.zoomIdentity;
      const xs = pts.map((p) => p.px);
      const ys = pts.map((p) => p.py);
      if (Number.isFinite(this.active.x) && Number.isFinite(this.active.y)) {
        xs.push(this.active.x);
        ys.push(this.active.y);
      }
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), maxY = Math.max(...ys);
      const boxW = Math.max(28, maxX - minX);
      const boxH = Math.max(28, maxY - minY);
      const pad = Math.min(86, Math.max(42, Math.min(this._w, this._h) * 0.18));
      const fit = Math.min((this._w - pad * 2) / boxW, (this._h - pad * 2) / boxH);
      const k = scale ?? Math.max(1.0, Math.min(2.4, fit / 2));
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      return d3.zoomIdentity.translate(this._w / 2 - cx * k, this._h / 2 - cy * k).scale(k);
    },
    setActiveFocusTransform() {
      // Follow the rotating passages without repeatedly changing the zoom.
      this._transform = this.activeFocusTransform(this._transform.k);
      this.syncZoomTransform();
    },
    syncZoomTransform() {
      // D3 must start the next gesture from the camera currently on screen.
      if (this.$refs.canvas) this.$refs.canvas.__zoom = this._transform;
    },
    animateToActive(duration = 900) {
      this.animateTransform(this.activeFocusTransform(), duration);
    },
    animateToFull(duration = 700) {
      this._keepActiveCentered = false;
      this.animateTransform(d3.zoomIdentity, duration);
    },
    animateTransform(to, duration = 700) {
      if (this._focusRaf) cancelAnimationFrame(this._focusRaf);
      const from = this._transform || d3.zoomIdentity;
      const startWidth = this._w || 1;
      const startHeight = this._h || 1;
      const start = performance.now();
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const syncZoom = (transform) => {
        const canvas = this.$refs.canvas;
        if (!canvas || !this._zoom) return;
        this._applyingZoomTransform = true;
        try {
          d3.select(canvas).call(this._zoom.transform, transform);
        } finally {
          this._applyingZoomTransform = false;
        }
      };
      const step = () => {
        const t = Math.min(1, (performance.now() - start) / duration);
        const e = ease(t);
        this._transform = d3.zoomIdentity
          .translate(
            (from.x + (to.x - from.x) * e) * (this._w || 1) / startWidth,
            (from.y + (to.y - from.y) * e) * (this._h || 1) / startHeight,
          )
          .scale(from.k + (to.k - from.k) * e);
        syncZoom(this._transform);
        this.draw();
        if (t < 1) this._focusRaf = requestAnimationFrame(step);
        else this._focusRaf = null;
      };
      this._focusRaf = requestAnimationFrame(step);
    },
    byId(id) {
      if (!this._byId) {
        this._byId = {};
        for (const p of this._points) this._byId[p.id] = p;
      }
      return this._byId[id];
    },
    startPulse() {
      if (this._raf) cancelAnimationFrame(this._raf);
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.draw();
        return;
      }
      const step = () => {
        this.draw();
        if (this.active && performance.now() - this.active.born < 1600) {
          this._raf = requestAnimationFrame(step);
        }
      };
      this._raf = requestAnimationFrame(step);
      setTimeout(() => this.draw(), 1700);
    },
    draw() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this._points.length) return;
      const ctx = canvas.getContext('2d');
      const pal = this.palette();
      const t = this._transform;
      const dpr = this._dpr || 1;

      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, this._w, this._h);

      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const intro = ease(this.introProgress());
      const r = 3.6 * Math.pow(t.k, 0.3);

      // Constellation edges from the query star to its nearest passages.
      if (this.active) {
        const age = (performance.now() - this.active.born) / 1600;
        const grow = ease(Math.min(1, age));
        const [sx, sy] = t.apply([this.active.x, this.active.y]);
        for (const h of this.active.hits) {
          const p = this.byId(h.id);
          if (!p) continue;
          const [hx, hy] = t.apply([p.px, p.py]);
          ctx.strokeStyle = pal.edge;
          ctx.globalAlpha = Math.max(0.1, h.score * 0.55) * grow;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(sx, sy);
          ctx.lineTo(sx + (hx - sx) * grow, sy + (hy - sy) * grow);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
      }

      // Points.
      const activeIds = this.active ? new Set(this.active.hits.map((h) => h.id)) : null;
      const drawPoints = this.viewMode === '3d'
        ? [...this._points].sort((a, b) => a.depth - b.depth)
        : this._points;
      drawPoints.forEach((p, i) => {
        const [x, y] = t.apply([p.px, p.py]);
        if (x < -20 || y < -20 || x > this._w + 20 || y > this._h + 20) return;
        const stagger = Math.min(1, Math.max(0, intro * 1.35 - (i / this._points.length) * 0.35));
        const isHit = activeIds && activeIds.has(p.id);
        const isHover = this._hover === p;
        const depthAlpha = this.viewMode === '3d' ? Math.max(0.28, Math.min(1, 0.68 + p.depth * 0.28)) : 1;
        const depthSize = this.viewMode === '3d' ? Math.max(0.52, Math.min(1.5, 1 + p.depth * 0.24)) : 1;
        ctx.globalAlpha = (isHit || isHover ? 0.95 : 0.72) * stagger * depthAlpha;
        ctx.fillStyle = pal[p.group];
        ctx.beginPath();
        ctx.arc(x, y, (isHover ? r + 2 : (isHit ? r + 1 : r)) * depthSize, 0, Math.PI * 2);
        ctx.fill();
        if (isHit) {
          ctx.globalAlpha = 0.35 * stagger;
          ctx.strokeStyle = pal.edge;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, r + 4, 0, Math.PI * 2);
          ctx.stroke();
        }
      });
      ctx.globalAlpha = 1;

      // Group labels, fixed size in screen space.
      ctx.font = '600 11px "Quattrocento Sans", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillStyle = pal.label;
      for (const c of this._centroids || []) {
        const [x, y] = t.apply([c.px, c.py]);
        ctx.globalAlpha = 0.55 * intro;
        ctx.fillText(c.label.toUpperCase(), x, y - 14);
      }
      ctx.globalAlpha = 1;

      // The query star.
      if (this.active) {
        const age = (performance.now() - this.active.born) / 1600;
        const grow = ease(Math.min(1, age));
        const [sx, sy] = t.apply([this.active.x, this.active.y]);
        if (age < 1) {
          ctx.globalAlpha = (1 - age) * 0.45;
          ctx.strokeStyle = pal.star;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(sx, sy, 6 + age * 26, 0, Math.PI * 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.fillStyle = pal.star;
        const R = (4.5 + Math.pow(t.k, 0.3)) * (0.6 + 0.4 * grow);
        ctx.beginPath();
        for (let k = 0; k < 8; k++) {
          const ang = (k * Math.PI) / 4;
          const rad = k % 2 === 0 ? R : R * 0.45;
          const px = sx + Math.cos(ang) * rad;
          const py = sy + Math.sin(ang) * rad;
          if (k === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
      }

      // Particle burst from the star to the hits.
      if (this._particles && this._particles.length && this.active) {
        const elapsed = performance.now() - this._particlesStart;
        const [sx, sy] = t.apply([this.active.x, this.active.y]);
        ctx.fillStyle = pal.star;
        for (const pt of this._particles) {
          const p = this.byId(pt.hitId);
          if (!p) continue;
          const [hx, hy] = t.apply([p.px, p.py]);
          const local = (elapsed - pt.delayMs) / (this._particlesDuration * pt.speed);
          if (local <= 0 || local >= 1) continue;
          const dx = hx - sx;
          const dy = hy - sy;
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const arc = Math.sin(Math.PI * local);
          const px = sx + dx * local + nx * pt.drift * arc;
          const py = sy + dy * local + ny * pt.drift * arc;
          ctx.globalAlpha = pt.alpha * arc;
          ctx.beginPath();
          ctx.arc(px, py, pt.size, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      ctx.restore();
    },
  },
};
</script>

<style scoped>
.emap {
  --emap-line: rgba(60, 60, 67, 0.12);
  --emap-muted: #757575;
  --emap-wash: rgba(0, 0, 0, 0.025);
}

.emap-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.emap-compact .emap-controls {
  margin: 0 12px 8px;
}

.emap-form {
  display: flex;
  flex: 1 1 260px;
  gap: 6px;
  max-height: 44px;
  min-height: 0;
}

.emap-input {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 7px 11px;
  border: 1px solid var(--emap-line);
  border-radius: 7px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.9rem;
}

.emap-input:focus {
  outline: none;
  border-color: var(--site-accent);
}
.emap-input::placeholder { color: #66666b; opacity: 1; }

.emap-go {
  box-sizing: border-box;
  padding: 7px 14px;
  border: 1px solid var(--emap-line);
  border-radius: 7px;
  background: transparent;
  color: inherit;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.84rem;
  cursor: pointer;
}

.emap-go:hover:not(:disabled) {
  border-color: var(--site-accent);
  color: var(--site-accent);
}

.emap-samples {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.emap-chip {
  padding: 4px 10px;
  border: 1px solid var(--emap-line);
  border-radius: 999px;
  background: transparent;
  color: #56565b;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  cursor: pointer;
}

.emap-chip:hover:not(:disabled) {
  border-color: var(--site-accent);
  color: var(--site-accent);
}

.emap-view-toggle {
  display: inline-flex;
  margin-left: auto;
  border: 1px solid var(--emap-line);
  border-radius: 6px;
  overflow: hidden;
}

.emap-view {
  min-width: 38px;
  padding: 5px 9px;
  border: none;
  border-right: 1px solid var(--emap-line);
  background: transparent;
  color: var(--emap-muted);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.7rem;
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease;
}

.emap-view:last-child {
  border-right: none;
}

.emap-view:hover {
  color: #4a4a4a;
  background: rgba(var(--site-accent-rgb), 0.08);
}

.emap-view.active,
.emap-view.active:hover {
  color: #2f2f2f;
  background: rgba(var(--site-accent-rgb), 0.14);
}

.emap-stage {
  position: relative;
  border: 0;
  border-radius: 10px;
  background: var(--emap-wash);
  overflow: hidden;
  transition: background-color 0.35s ease, border-color 0.35s ease;
}

.emap-compact .emap-stage {
  border-left: none;
  border-right: none;
  border-radius: 0;
}

.emap-canvas {
  display: block;
  width: 100%;
  cursor: var(--site-grab-cursor, grab);
  touch-action: none;
}

.emap-two-finger-touch .emap-canvas {
  touch-action: pan-y;
}

.emap-tooltip {
  position: absolute;
  max-width: 200px;
  padding: 6px 9px;
  border: 1px solid var(--emap-line);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.95);
  color: inherit;
  cursor: pointer;
  pointer-events: auto;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 1px;
  transition: background-color 0.35s ease, border-color 0.35s ease, color 0.35s ease;
}

.emap-tooltip:hover {
  border-color: var(--site-accent);
}

.emap-tooltip-group {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.64rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--emap-muted);
}

.emap-tooltip-title {
  font-size: 0.8rem;
  line-height: 1.3;
}

.emap-status {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 8px;
  margin: 0;
  text-align: center;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  color: var(--emap-muted);
}

.emap-hits {
  margin-top: 10px;
}

.emap-compact .emap-hits {
  margin: 10px 14px 0;
}

.emap-hits-label {
  margin: 0 0 4px;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.76rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--emap-muted);
}

.emap-hits ol {
  margin: 0;
  padding-left: 1.2rem;
}

.emap-hits li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  column-gap: 8px;
  font-size: 0.86rem;
  margin: 2px 0;
}

.emap-hits button {
  justify-self: start;
  min-width: 0;
  border: none;
  background: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(var(--site-accent-rgb), 0.5);
  text-underline-offset: 2px;
}

.emap-hits button:hover {
  color: var(--site-accent);
}

.emap-hit-score {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  color: var(--emap-muted);
}

.emap-caption {
  margin: 10px 0 0;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--emap-muted);
  opacity: 0.72;
}

body.dark-mode .emap {
  --emap-line: rgba(255, 255, 255, 0.16);
  --emap-muted: var(--site-text-tertiary);
  --emap-wash: rgba(255, 255, 255, 0.035);
}
body.dark-mode .emap-input::placeholder { color: var(--site-text-secondary); }
body.dark-mode .emap-chip { color: var(--site-text-secondary); }
body.dark-mode .emap-caption { color: #8e8e93; opacity: 0.72; }

body.dark-mode .emap-view:hover {
  color: #c6c6c6;
  background: rgba(var(--site-accent-rgb), 0.09);
}

body.dark-mode .emap-view.active,
body.dark-mode .emap-view.active:hover {
  color: #e1e1e1;
  background: rgba(var(--site-accent-rgb), 0.16);
}

body.dark-mode .emap-tooltip {
  background: rgba(24, 24, 24, 0.95);
}

:global(html.theme-transitioning .emap-view-toggle),
:global(body.theme-transitioning .emap-view-toggle),
:global(html.theme-transitioning .emap-view),
:global(body.theme-transitioning .emap-view),
:global(html.theme-transitioning .emap-stage),
:global(body.theme-transitioning .emap-stage),
:global(html.theme-transitioning .emap-tooltip),
:global(body.theme-transitioning .emap-tooltip) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease,
    box-shadow var(--theme-duration, 220ms) ease;
}

@media (max-width: 700px) {
  .emap-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .emap-form {
    flex: 0 1 auto;
  }

  .emap-view-toggle {
    margin-left: 0;
    align-self: flex-start;
  }
}

@container (max-width: 700px) {
  .emap-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .emap-form {
    flex: 0 1 auto;
  }

  .emap-view-toggle {
    margin-left: 0;
    align-self: flex-start;
  }
}
</style>
